import { conform, useForm } from '@conform-to/react'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import {
	data,
	type ActionFunctionArgs,
	redirect,
	type MetaFunction,
	Form,
	Link,
	useActionData,
	useSearchParams,
} from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { Divider } from '#app/components/divider.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { ProviderConnectionForm, providerNames } from '#app/utils/connections.tsx'
import { validateCSRF } from '#app/utils/csrf.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import {
	EmailWithTheme,
	EmailHeader,
	EmailContent,
	EmailTitle,
	EmailText,
	EmailCode,
	EmailButton,
	EmailDivider,
	EmailLink,
	EmailInfoBox,
} from '#app/utils/email-ui.tsx'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { EmailSchema } from '#app/utils/user-validation.ts'
import { prepareVerification } from './utils/functions.server.ts'

const SignupSchema = z.object({
	email: EmailSchema,
})

export async function action({ request, url }: ActionFunctionArgs) {
	let formData: FormData | null = null
	try {
		formData = await request.formData()
	} catch (error) {
		throw new Response('Invalid payload', { status: 400 })
	}

	await validateCSRF(formData, request.headers)
	await checkHoneypot(formData)

	const submission = await parse(formData, {
		schema: SignupSchema.superRefine(async (data, ctx) => {
			const existingUser = await prisma.user.findUnique({
				where: { email: data.email },
				select: { id: true },
			})
			if (existingUser) {
				ctx.addIssue({
					path: ['email'],
					code: z.ZodIssueCode.custom,
					message: 'Ya existe un usuario con este correo electrónico',
				})
				return
			}
		}),
		async: true,
	})
	if (submission.intent !== 'submit') {
		return data({ status: 'idle', submission } as const)
	}
	if (!submission.value) {
		return data({ status: 'error', submission } as const, { status: 400 })
	}
	const { email } = submission.value
	const requestUrl = url
	const redirectToParam = requestUrl.searchParams.get('redirectTo') ?? undefined
	let { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'onboarding',
		target: email,
		redirectTo: redirectToParam,
	})

	const response = await sendEmail({
		to: email,
		subject: `Bienvenido a IusPro!`,
		react: <SignupEmail onboardingUrl={verifyUrl.toString()} otp={otp} />,
	})

	if (response.status === 'success') {
		return redirect(redirectTo.toString())
	} else {
		submission.error[''] = [response.error.message]
		return data({ status: 'error', submission } as const, { status: 500 })
	}
}

export function SignupEmail({ onboardingUrl, otp }: { onboardingUrl: string; otp: string }) {
	return (
		<EmailWithTheme title="¡Bienvenido a IusPro!">
			<EmailHeader title="IusPro" />
			<EmailContent>
				<EmailTitle>¡Bienvenido a IusPro!</EmailTitle>
				<EmailText>
					Nos alegra que te unas a nosotros. Estás a un paso de acceder a tu asistente legal con inteligencia artificial
					diseñado específicamente para abogados costarricenses.
				</EmailText>
				<EmailText>Para completar tu registro, ingresa este código de verificación:</EmailText>
				<EmailCode code={otp} />
				<EmailText>Este código expirará en 10 minutos por seguridad.</EmailText>
				<EmailDivider />
				<EmailText>También puedes completar tu registro directamente haciendo clic en el siguiente botón:</EmailText>
				<EmailButton href={onboardingUrl}>Completar Registro</EmailButton>
				<EmailText>Si el botón no funciona, copia y pega este enlace en tu navegador:</EmailText>
				<EmailLink href={onboardingUrl}>{onboardingUrl}</EmailLink>
				<EmailInfoBox title="¿Qué puedes hacer con IusPro?">
					Accede a análisis legal con IA, generación de documentos, transcripción de audiencias y mucho más. Todo
					diseñado para optimizar tu práctica legal en Costa Rica.
				</EmailInfoBox>
			</EmailContent>
		</EmailWithTheme>
	)
}

export const meta: MetaFunction = () => {
	return [{ title: 'Crea tu cuenta | IusPro' }]
}

export default function SignupRoute() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')
	const comingFromExam = redirectTo?.includes('/examenes-de-practica/') && redirectTo?.endsWith('/resultados')

	const [form, fields] = useForm({
		id: 'signup-form',
		constraint: getFieldsetConstraint(SignupSchema),
		lastSubmission: actionData?.submission,
		onValidate({ formData }) {
			const result = parse(formData, { schema: SignupSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="flex w-full max-w-lg flex-col justify-start">
			<div className="flex flex-col gap-3 text-center">
				<h1 className="text-h4 tracking-tighter md:text-h2">Crea tu cuenta</h1>
				<p className="text-md text-muted-foreground">
					{comingFromExam ? 'Solo toma unos segundos.' : 'Por favor, ingresa tu correo electrónico.'}
				</p>
			</div>
			{comingFromExam && (
				<div className="mt-8 overflow-hidden rounded-2xl border border-neutral-200 bg-gradient-to-b from-neutral-50 to-white">
					<div className="border-b border-neutral-100 bg-neutral-900 px-5 py-3">
						<div className="flex items-center gap-2">
							<Icon name="exam" className="h-4 w-4 text-white/70" />
							<span className="text-sm font-medium text-white">Examen completado</span>
						</div>
					</div>
					<div className="px-5 py-4">
						<p className="text-[15px] font-medium leading-snug text-neutral-900">Tus resultados están listos</p>
						<p className="mt-1.5 text-sm leading-relaxed text-neutral-500">
							Crea una cuenta gratuita para desbloquear el desglose completo con explicaciones de cada respuesta.
						</p>
						<div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
							<span className="flex items-center gap-1.5 text-xs text-neutral-400">
								<Icon name="check" className="h-3 w-3 text-neutral-900" />
								Respuestas correctas
							</span>
							<span className="flex items-center gap-1.5 text-xs text-neutral-400">
								<Icon name="check" className="h-3 w-3 text-neutral-900" />
								Explicaciones legales
							</span>
						</div>
					</div>
				</div>
			)}
			<div className="mx-auto mt-16 min-w-full sm:min-w-[368px]">
				<Form method="POST" {...form.props}>
					<HoneypotInputs />
					<Field
						labelProps={{
							htmlFor: fields.email.id,
							children: 'Email',
						}}
						inputProps={{
							...conform.input(fields.email),
							autoFocus: true,
							placeholder: 'correo@gmail.com...',
							className: 'h-12',
							icon: <Icon name="envelope-closed" />,
						}}
						errors={fields.email.errors}
					/>
					<ErrorList errors={form.errors} id={form.errorId} />
					<StatusButton
						className="h-12 w-full rounded-xl bg-neutral-900 px-4 py-3 hover:hover:bg-neutral-600"
						status={isPending ? 'pending' : (actionData?.status ?? 'idle')}
						type="submit"
						disabled={isPending}
					>
						Continuar
						<Icon name="arrow-right" className="ml-1 h-4 w-4" />
					</StatusButton>
				</Form>
				<Divider className="my-6" text="O conectar con" />
				<ul className="flex flex-col gap-5">
					{providerNames.map(providerName => (
						<li key={providerName}>
							<ProviderConnectionForm type="Signup" providerName={providerName} redirectTo={redirectTo} />
						</li>
					))}
				</ul>
				<div className="pt-6">
					<span className="flex flex-row items-center gap-3">
						<span className="text-sm text-muted-foreground/60">Ya tienes una cuenta?</span>{' '}
						<Button asChild variant="outline" className="h-auto rounded-full px-3 py-2 text-xs">
							<Link to={redirectTo ? `/login?redirectTo=${encodeURIComponent(redirectTo)}` : '/login'}>
								Inicia sesión
							</Link>
						</Button>
					</span>
				</div>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
